import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

async function generateUniqueSlug(baseSlug) {
  let slug = baseSlug || 'coach';
  let attempts = 0;
  const maxAttempts = 10;

  while (attempts < maxAttempts) {
    const existing = await getCoachBySlug(slug);
    if (!existing) return slug;
    slug = `${baseSlug || 'coach'}-${Math.random().toString(36).slice(2, 8)}`;
    attempts++;
  }
  return `${baseSlug || 'coach'}-${Date.now()}`;
}

/**
 * Create a new coach entity (single-team)
 */
export async function createCoach(ownerId, coachData) {
  const baseSlug = slugify(coachData.name || 'coach');
  const slug = await generateUniqueSlug(baseSlug);

  const coachRef = doc(collection(db, 'coaches'));
  const coachId = coachRef.id;

  const coach = {
    id: coachId,
    name: coachData.name || 'My Team',
    ownerId,
    slug,
    teamName: coachData.teamName || coachData.name || 'My Team',
    ageGroup: coachData.ageGroup || null,
    markupPerMonth: coachData.markupPerMonth ?? 0,
    stripeAccountId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(coachRef, coach);

  const { getUserProfile, updateUserProfile } = await import('./database');
  const existing = await getUserProfile(ownerId);
  await updateUserProfile(ownerId, {
    coachId,
    role: existing?.role === 'club_owner' ? existing.role : 'coach',
  });

  return { coachId, slug, coach: { ...coach, id: coachId } };
}

export async function getCoach(coachId) {
  if (!coachId) return null;
  const snap = await getDoc(doc(db, 'coaches', coachId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function getCoachBySlug(slug) {
  if (!slug) return null;
  const q = query(
    collection(db, 'coaches'),
    where('slug', '==', slug)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

export async function updateCoach(coachId, updates) {
  const coachRef = doc(db, 'coaches', coachId);
  await updateDoc(coachRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
  return getCoach(coachId);
}

/**
 * Create a membership for a player under a coach
 */
export async function createCoachMembership(userId, coachId) {
  const year = new Date().getFullYear();
  const expiresAt = new Date(year, 11, 31);
  const membershipRef = await addDoc(collection(db, 'club_memberships'), {
    userId,
    clubId: null,
    coachId,
    teamId: null,
    year,
    status: 'active',
    stripeSubscriptionId: null,
    joinedAt: serverTimestamp(),
    expiresAt: expiresAt.toISOString().split('T')[0],
  });
  return membershipRef.id;
}

/**
 * Get active memberships for a coach
 */
export async function getCoachMemberships(coachId, status = 'active') {
  const q = query(
    collection(db, 'club_memberships'),
    where('coachId', '==', coachId),
    where('status', '==', status)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
